import { ColumnDef } from '@tanstack/react-table';
import { KioskSessionGridRow, OcpiAuthMethod, OcpiPowerType } from '../types/kiosk-session-types';
import { Badge } from '@/core/components/ui/badge';

/**
 * Helper function to determine badge variant based on session status
 */
function getStatusVariant(status: string): 'default' | 'success' | 'destructive' | 'secondary' {
  switch (status?.toUpperCase()) {
    case 'COMPLETED': return 'success';
    case 'ACTIVE': return 'default';
    case 'FAILED': return 'destructive';
    default: return 'secondary';
  }
}

/**
 * Column definitions for Kiosk Sessions Grid
 * Updated columns: status, evseId, connectorId, cdrId
 */
export const kioskSessionsColumns: ColumnDef<KioskSessionGridRow>[] = [
  {
    id: 'session_id',
    accessorKey: 'session_id',
    header: 'Session ID',
    size: 100,
    cell: ({ getValue }) => {
      const value = getValue<string>();
      return (
        <span className="font-mono text-sm">
          {value || 'N/A'}
        </span>
      );
    },
    enableSorting: true,
    sortingFn: 'alphanumeric',
    meta: {
      align: 'left',
    },
  },
  {
    id: 'status',
    accessorKey: 'status',
    header: 'Status',
    size: 100,
    cell: ({ getValue }) => {
      const value = getValue<string>();
      const variant = getStatusVariant(value);
      return (
        <div className="flex justify-center">
          <Badge variant={variant}>
            {value || 'UNKNOWN'}
          </Badge>
        </div>
      );
    },
    enableSorting: true,
    sortingFn: 'alphanumeric',
    meta: {
      align: 'center',
    },
  },
  {
    id: 'evse_id',
    accessorKey: 'evse_id',
    header: 'EVSE ID',
    size: 120,
    cell: ({ getValue }) => {
      const value = getValue<string>();
      return (
        <span className="text-sm font-medium">
          {value || 'Unknown'}
        </span>
      );
    },
    enableSorting: true,
    sortingFn: 'alphanumeric',
    meta: {
      align: 'center',
    },
  },
  {
    id: 'connector_id',
    accessorKey: 'connector_id',
    header: 'Connector ID',
    size: 100,
    cell: ({ getValue }) => {
      const value = getValue<string>();
      return (
        <span className="text-sm">
          {value || 'N/A'}
        </span>
      );
    },
    enableSorting: true,
    sortingFn: 'alphanumeric',
    meta: {
      align: 'center',
    },
  },
  {
    id: 'start_time',
    accessorKey: 'start_time',
    header: 'Start Time',
    size: 160,
    cell: ({ getValue }) => {
      const value = getValue<Date>();
      return (
        <span className="text-sm">
          {formatDateTime(value)}
        </span>
      );
    },
    enableSorting: true,
    sortingFn: 'datetime',
    meta: {
      align: 'left',
    },
  },
  {
    id: 'stop_time',
    accessorKey: 'stop_time',
    header: 'End Time',
    size: 160,
    cell: ({ getValue }) => {
      const value = getValue<Date | null>();
      return (
        <span className="text-sm">
          {value ? formatDateTime(value) : ''}
        </span>
      );
    },
    enableSorting: true,
    sortingFn: 'datetime',
    meta: {
      align: 'left',
    },
  },
  {
    id: 'duration',
    accessorFn: (row) => {
      if (row.start_time && row.stop_time) {
        const start = new Date(row.start_time);
        const stop = new Date(row.stop_time);
        return stop.getTime() - start.getTime();
      }
      return 0;
    },
    header: 'Duration',
    size: 100,
    cell: ({ row }) => {
      const start = row.original.start_time;
      const stop = row.original.stop_time;
      return (
        <span className="text-sm font-medium">
          {formatDuration(start, stop)}
        </span>
      );
    },
    enableSorting: true,
    sortingFn: 'basic',
    meta: {
      align: 'right',
    },
  },
  {
    id: 'total_energy',
    accessorKey: 'total_energy',
    header: 'Total Energy',
    size: 110,
    cell: ({ getValue }) => {
      const value = getValue<number>();
      return (
        <span className="text-sm font-medium">
          {formatEnergy(value)}
        </span>
      );
    },
    enableSorting: true,
    sortingFn: 'basic',
    meta: {
      align: 'right',
    },
  },
  {
    id: 'cdr_id',
    accessorKey: 'cdr_id',
    header: 'CDR',
    size: 90,
    cell: ({ getValue }) => {
      const value = getValue<string>();
      return (
        <span className="font-mono text-sm">
          {value || ''}
        </span>
      );
    },
    enableSorting: true,
    sortingFn: 'alphanumeric',
    meta: {
      align: 'center',
    },
  },
  // ===== NEW TRANSACTION FIELDS (HIVE-6397) =====
  {
    id: 'auth_amount',
    accessorKey: 'authAmount',
    header: 'Auth Amount',
    size: 100,
    cell: ({ getValue, row, table }) => {
      const value = getValue<number>();
      const currency = isValidCurrencyCode(row.original.currency)
        ? row.original.currency
        : (table.options.meta as any)?.fallbackCurrency;
      return (
        <span className="text-sm font-medium">
          {formatCurrency(value, currency)}
        </span>
      );
    },
    enableSorting: true,
    sortingFn: 'basic',
    meta: {
      align: 'right',
    },
  },
  {
    id: 'settlement_amount',
    accessorKey: 'settAmount',
    header: 'Settlement Amount',
    size: 100,
    cell: ({ getValue, row, table }) => {
      const value = getValue<number>();
      const currency = isValidCurrencyCode(row.original.currency)
        ? row.original.currency
        : (table.options.meta as any)?.fallbackCurrency;
      return (
        <span className="text-sm font-medium">
          {formatCurrency(value, currency)}
        </span>
      );
    },
    enableSorting: true,
    sortingFn: 'basic',
    meta: {
      align: 'right',
    },
  },
  {
    id: 'total_inc_vat',
    accessorKey: 'totalIncVat',
    header: 'Total inc VAT',
    size: 110,
    cell: ({ getValue, row, table }) => {
      const value = getValue<number>();
      const currency = isValidCurrencyCode(row.original.currency)
        ? row.original.currency
        : (table.options.meta as any)?.fallbackCurrency;
      return (
        <span className="text-sm font-medium">
          {formatCurrency(value, currency)}
        </span>
      );
    },
    enableSorting: true,
    sortingFn: 'basic',
    meta: {
      align: 'right',
    },
  },
  {
    id: 'total_exc_vat',
    accessorKey: 'totalExcVat',
    header: 'Total exc VAT',
    size: 110,
    cell: ({ getValue, row, table }) => {
      const value = getValue<number>();
      const currency = isValidCurrencyCode(row.original.currency)
        ? row.original.currency
        : (table.options.meta as any)?.fallbackCurrency;
      return (
        <span className="text-sm font-medium">
          {formatCurrency(value, currency)}
        </span>
      );
    },
    enableSorting: true,
    sortingFn: 'basic',
    meta: {
      align: 'right',
    },
  },
  {
    id: 'vat_amount',
    accessorKey: 'vatAmount',
    header: 'VAT Amount',
    size: 100,
    cell: ({ getValue, row, table }) => {
      const value = getValue<number>();
      const currency = isValidCurrencyCode(row.original.currency)
        ? row.original.currency
        : (table.options.meta as any)?.fallbackCurrency;
      return (
        <span className="text-sm font-medium">
          {formatCurrency(value, currency)}
        </span>
      );
    },
    enableSorting: true,
    sortingFn: 'basic',
    meta: {
      align: 'right',
    },
  },
  {
    id: 'payment_method',
    accessorKey: 'paymentMethod',
    header: 'Payment Method',
    size: 120,
    cell: ({ getValue }) => {
      const value = getValue<string>();
      return (
        <span className="text-sm">
          {formatPaymentMethod(value)}
        </span>
      );
    },
    enableSorting: true,
    sortingFn: 'alphanumeric',
    meta: {
      align: 'left',
    },
  },
  {
    id: 'card_identifier',
    accessorKey: 'cardIdentifier',
    header: 'Card Identifier',
    size: 130,
    cell: ({ getValue }) => {
      const value = getValue<string>();
      return (
        <span className="font-mono text-sm">
          {value ? `****${value}` : ''}
        </span>
      );
    },
    enableSorting: true,
    sortingFn: 'alphanumeric',
    meta: {
      align: 'left',
    },
  },
  {
    id: 'machine_name',
    accessorKey: 'machine_name',
    header: 'Machine Name',
    size: 160,
    cell: ({ getValue }) => {
      const value = getValue<string>();
      return (
        <span className="text-sm font-medium">
          {value || 'N/A'}
        </span>
      );
    },
    enableSorting: true,
    sortingFn: 'alphanumeric',
    meta: {
      align: 'left',
    },
  },
  {
    id: 'payment_transaction_id',
    accessorKey: 'payment_transaction_id',
    header: 'Transaction ID',
    size: 120,
    cell: ({ getValue }) => {
      const value = getValue<string>();
      return (
        <span className="font-mono text-sm">
          {value || 'N/A'}
        </span>
      );
    },
    enableSorting: true,
    sortingFn: 'alphanumeric',
    meta: {
      align: 'left',
    },
  },
  // ===== NEW COLUMNS (HIVE-6390) =====
  {
    id: 'auth_method',
    accessorKey: 'auth_method',
    header: 'Auth Method',
    size: 110,
    cell: ({ getValue }) => {
      const value = getValue<OcpiAuthMethod>();
      return (
        <span className="text-sm">
          {formatAuthMethod(value)}
        </span>
      );
    },
    enableSorting: true,
    sortingFn: 'alphanumeric',
    meta: {
      align: 'center',
    },
  },
  {
    id: 'connector_standard',
    accessorKey: 'connector_standard',
    header: 'Connector Type',
    size: 120,
    cell: ({ getValue }) => {
      const value = getValue<string>();
      return (
        <span className="text-sm">
          {formatConnectorStandard(value)}
        </span>
      );
    },
    enableSorting: true,
    sortingFn: 'alphanumeric',
    meta: {
      align: 'center',
    },
  },
  {
    id: 'max_power',
    accessorKey: 'max_power',
    header: 'Max Power',
    size: 100,
    cell: ({ getValue }) => {
      const value = getValue<number>();
      return (
        <span className="text-sm font-medium">
          {formatPower(value)}
        </span>
      );
    },
    enableSorting: true,
    sortingFn: 'basic',
    meta: {
      align: 'right',
    },
  },
  {
    id: 'power_type',
    accessorKey: 'power_type',
    header: 'Power Type',
    size: 100,
    cell: ({ getValue }) => {
      const value = getValue<OcpiPowerType>();
      return (
        <span className="text-sm">
          {formatPowerType(value)}
        </span>
      );
    },
    enableSorting: true,
    sortingFn: 'alphanumeric',
    meta: {
      align: 'center',
    },
  },
  {
    id: 'receipt',
    accessorKey: 'receipt',
    header: 'Receipt',
    size: 80,
    cell: ({ getValue }) => {
      const value = getValue<string>();
      if (!value) return <span className="text-sm"></span>;
      return (
        <a
          href={value}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm text-blue-600 hover:underline"
        >
          View
        </a>
      );
    },
    enableSorting: false,
    meta: {
      align: 'center',
    },
  },
  {
    id: 'location_name',
    accessorKey: 'location_name',
    header: 'Location',
    size: 160,
    cell: ({ getValue }) => {
      const value = getValue<string>();
      return (
        <span className="text-sm">
          {value || 'N/A'}
        </span>
      );
    },
    enableSorting: true,
    sortingFn: 'alphanumeric',
    meta: {
      align: 'left',
    },
  },
  // ===== PS-158: Phone Number & License Plate (empty cell when not collected) =====
  {
    id: 'phone_number',
    accessorKey: 'phone_number',
    header: 'Phone Number',
    size: 140,
    cell: ({ getValue }) => {
      const value = getValue<string>();
      return (
        <span className="text-sm">
          {value || ''}
        </span>
      );
    },
    enableSorting: true,
    sortingFn: 'alphanumeric',
    meta: {
      align: 'left',
    },
  },
  {
    id: 'plate',
    accessorKey: 'plate',
    header: 'License Plate',
    size: 120,
    cell: ({ getValue }) => {
      const value = getValue<string>();
      return (
        <span className="font-mono text-sm">
          {value || ''}
        </span>
      );
    },
    enableSorting: true,
    sortingFn: 'alphanumeric',
    meta: {
      align: 'left',
    },
  },
];

/**
 * Formats Date object for display in grid
 */
function formatDateTime(date: Date): string {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
    return 'Invalid date';
  }

  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
}

/**
 * Formats duration between two dates
 */
function formatDuration(startTime?: Date, stopTime?: Date): string {
  if (!startTime || !stopTime || !(startTime instanceof Date) || !(stopTime instanceof Date) ||
      isNaN(startTime.getTime()) || isNaN(stopTime.getTime())) {
    return '';
  }

  const durationMs = stopTime.getTime() - startTime.getTime();

  if (durationMs < 0) {
    return '';
  }

  const hours = Math.floor(durationMs / (1000 * 60 * 60));
  const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));

  if (hours === 0 && minutes === 0) {
    const seconds = Math.floor((durationMs % (1000 * 60)) / 1000);
    return `${seconds}s`;
  }

  if (hours === 0) {
    return `${minutes}m`;
  }

  return `${hours}h ${minutes}m`;
}

/**
 * Formats energy value with kWh unit (2 decimal places)
 */
function formatEnergy(energy: number): string {
  if (typeof energy !== 'number' || isNaN(energy)) {
    return '0.00 kWh';
  }

  return `${energy.toFixed(2)} kWh`;
}

/**
 * Validates an ISO 4217 currency code (3 uppercase letters that Intl recognizes)
 */
function isValidCurrencyCode(code: string | undefined): code is string {
  if (!code || !/^[A-Z]{3}$/.test(code)) return false;
  try {
    new Intl.NumberFormat('en', { style: 'currency', currency: code });
    return true;
  } catch {
    return false;
  }
}

function formatCurrency(value: number, currencyCode?: string): string {
  const amount = typeof value === 'number' && !isNaN(value) ? value : 0;
  if (!currencyCode) {
    return new Intl.NumberFormat('en', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  }
  return new Intl.NumberFormat('en-IL', {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
}

/**
 * Formats auth method for display
 */
function formatAuthMethod(method: OcpiAuthMethod | undefined): string {
  if (!method || method === 'UNKNOWN') return 'N/A';
  const methodMap: Record<string, string> = {
    'AUTH_REQUEST': 'Auth Request',
    'COMMAND': 'Command',
    'WHITELIST': 'Whitelist'
  };
  return methodMap[method] || method;
}

/**
 * Formats connector standard for display
 */
function formatConnectorStandard(standard: string | undefined): string {
  if (!standard || standard === 'Unknown') return 'N/A';
  // Make connector type more readable
  return standard
    .replace('IEC_62196_', '')
    .replace('_COMBO', ' Combo')
    .replace(/_/g, ' ');
}

/**
 * Formats power value for display
 */
function formatPower(power: number | undefined): string {
  if (power == null || isNaN(power)) return 'N/A';
  return `${power} kW`;
}

/**
 * Formats power type for display
 */
function formatPowerType(powerType: OcpiPowerType | undefined): string {
  if (!powerType || powerType === 'UNKNOWN') return 'N/A';
  const powerMap: Record<string, string> = {
    'AC_1_PHASE': 'AC 1-Phase',
    'AC_2_PHASE': 'AC 2-Phase',
    'AC_2_PHASE_SPLIT': 'AC Split',
    'AC_3_PHASE': 'AC 3-Phase',
    'DC': 'DC'
  };
  return powerMap[powerType] || powerType;
}

/**
 * Formats payment method code to readable string
 */
function formatPaymentMethod(method: string | undefined): string {
  // Payment method is already mapped in the transformer (1 -> Credit Card, 2 -> Prepaid)
  if (!method || method === 'Unknown') return '';
  return method;
}

/**
 * Column configuration for responsive breakpoints
 */
export const responsiveColumnConfig = {
  mobile: ['session_id', 'status', 'evse_id', 'total_energy'],
  tablet: ['session_id', 'status', 'evse_id', 'connector_id', 'start_time', 'total_energy'],
  desktop: kioskSessionsColumns.map(col => ('id' in col ? col.id : '')).filter(Boolean)
};
/**
 * Default sorting configuration
 */
export const defaultSorting = [
  {
    id: 'start_time',
    desc: true // Most recent sessions first
  }
];

/**
 * Available columns for column filter
 */
export const availableColumns = [
  { id: 'session_id', header: 'Session ID' },
  { id: 'status', header: 'Status' },
  { id: 'evse_id', header: 'EVSE ID' },
  { id: 'connector_id', header: 'Connector ID' },
  { id: 'cdr_id', header: 'CDR' },
  { id: 'start_time', header: 'Start Time' },
  { id: 'stop_time', header: 'End Time' },
  { id: 'duration', header: 'Duration' },
  { id: 'total_energy', header: 'Total Energy' },
  // NEW TRANSACTION FIELDS (HIVE-6397)
  { id: 'auth_amount', header: 'Auth Amount' },
  { id: 'settlement_amount', header: 'Settlement Amount' },
  { id: 'total_inc_vat', header: 'Total inc VAT' },
  { id: 'total_exc_vat', header: 'Total exc VAT' },
  { id: 'vat_amount', header: 'VAT Amount' },
  { id: 'payment_method', header: 'Payment Method' },
  { id: 'card_identifier', header: 'Card Identifier' },
  { id: 'machine_name', header: 'Machine Name' },
  { id: 'payment_transaction_id', header: 'Transaction ID' },
  // NEW COLUMNS (HIVE-6390)
  { id: 'auth_method', header: 'Auth Method' },
  { id: 'connector_standard', header: 'Connector Type' },
  { id: 'max_power', header: 'Max Power' },
  { id: 'power_type', header: 'Power Type' },
  { id: 'receipt', header: 'Receipt' },
  { id: 'location_name', header: 'Location' },
  // PS-158
  { id: 'phone_number', header: 'Phone Number' },
  { id: 'plate', header: 'License Plate' },
];

/**
 * Default column visibility - all columns visible by default
 */
export const defaultColumnVisibility = {
  session_id: true,
  status: true,
  evse_id: true,
  connector_id: true,
  cdr_id: true,
  start_time: true,
  stop_time: true,
  duration: true,
  total_energy: true,
  // NEW TRANSACTION FIELDS (HIVE-6397)
  auth_amount: true,
  settlement_amount: true,
  total_inc_vat: true,
  total_exc_vat: false,
  vat_amount: true,
  payment_method: false,
  card_identifier: false,
  connector_alias: false,
  machine_name: false,
  payment_transaction_id: false,
  // NEW COLUMNS (HIVE-6390) - hidden by default (data only available in detail API)
  auth_method: false,
  connector_standard: false,
  max_power: false,
  power_type: false,
  receipt: false,
  location_name: false,
  // PS-158
  phone_number: false,
  plate: false,
};